import React from 'react';
import { motion } from 'framer-motion';
export interface PercentageBarProps {
  /** The percentage to display (0-100) */
  percentage: number;
  /** Optional label above the bar */
  label?: string;
  /** Optional second percentage for comparison */
  comparePercentage?: number;
  /** Labels for the percentages */
  percentageLabel?: string;
  compareLabel?: string;
  /** Show percentage text on the bar */
  showValue?: boolean;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
function formatPercent(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0
  }).format(value);
}
function SingleBar({
  percentage,
  color,
  barLabel,
  showValue = true,
  delay = 0
}: {
  percentage: number;
  color: string;
  barLabel?: string;
  showValue?: boolean;
  delay?: number;
}) {
  const clamped = clamp(percentage, 0, 100);
  return <div className="space-y-1.5" data-id="element-13941" data-mp="9usfnoraob2b">
      {barLabel && <div className="text-xs font-medium text-slate-600" data-id="element-13942" data-mp="9w2h8nxcw1dp">{barLabel}</div>}
      <div className="relative h-8 bg-slate-100 rounded-lg overflow-hidden border border-slate-200" data-id="element-13943" data-mp="etc3jupg7wxb">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${clamped}%`
      }} transition={{
        delay,
        duration: 0.6,
        ease: 'easeOut'
      }} className={`absolute inset-y-0 left-0 rounded-lg ${color}`} data-id="element-13944" data-mp="12iwqeooq3ebv" />
        
        {/* Grid lines at 25%, 50%, 75% */}
        {[25, 50, 75].map(mark => <div key={mark} className="absolute inset-y-0 w-px bg-slate-300/50" style={{
        left: `${mark}%`
      }} data-id="element-13945" data-mp="xr55ous902tn" />)}
        {/* Value label */}
        {showValue && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: delay + 0.4
      }} className="absolute inset-0 flex items-center justify-center text-sm font-bold" data-id="element-13946" data-mp="1q9k63w4vr88z">
          
            <span className={clamped > 45 ? 'text-white' : 'text-slate-700'} data-id="element-13947" data-mp="1v6pmcr7ibpjz">
              {formatPercent(clamped)}%
            </span>
          </motion.div>}
      </div>
      {/* Scale markers */}
      <div className="flex justify-between text-[10px] text-slate-400 px-0.5" data-id="element-13948" data-mp="1m4yilrwj1ssr">
        <span data-id="element-13949" data-mp="1h6x0paau7ziv">0%</span>
        <span data-id="element-13950" data-mp="1h7g8vzaor9jp">25%</span>
        <span data-id="element-13951" data-mp="1cfeyyk0zpuwu">50%</span>
        <span data-id="element-13952" data-mp="1cewskukm60b7">75%</span>
        <span data-id="element-13953" data-mp="1cen275vnpr4z">100%</span>
      </div>
    </div>;
}
export function PercentageBar({
  percentage,
  label,
  comparePercentage,
  percentageLabel,
  compareLabel,
  showValue = true,
  mode = 'support'
}: PercentageBarProps) {
  if (!isFiniteNumber(percentage)) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13954" data-mp="yfdvcwtcc9do">
        <p className="text-red-600 text-sm font-medium" data-id="element-13955" data-mp="tgkj17psiof3">
          Ongeldige percentage waarde
        </p>
      </div>;
  }
  const hasCompare = isFiniteNumber(comparePercentage);
  const clamped = clamp(percentage, 0, 100);
  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-5 py-4 border border-blue-200" data-id="element-13956" data-mp="3a4twcmvh3gj2">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-13957" data-mp="3ezzgph988n51">
            {label}
          </p>}
        <SingleBar percentage={clamped} color="bg-blue-500" barLabel={percentageLabel} showValue={showValue} data-id="element-13958" data-mp="3j1clrp4hwmaw" />
        
        {hasCompare && <div className="mt-3" data-id="element-13959" data-mp="2rbpzksmyknk8">
            <SingleBar percentage={clamp(comparePercentage!, 0, 100)} color="bg-amber-500" barLabel={compareLabel} showValue={showValue} delay={0.2} data-id="element-13960" data-mp="2rd110lsxlj86" />
          
          </div>}
      </div>;
  }
  // Support mode: educational panel
  const fractionText = (() => {
    if (clamped === 25) return '¼';
    if (clamped === 50) return '½';
    if (clamped === 75) return '¾';
    if (clamped === 10) return '¹⁄₁₀';
    if (clamped === 20) return '¹⁄₅';
    if (clamped === 33 || clamped === 33.3) return '⅓';
    if (clamped === 40) return '⅖';
    if (clamped === 60) return '⅗';
    if (clamped === 80) return '⅘';
    if (clamped === 66 || clamped === 66.6 || clamped === 66.7) return '⅔';
    return null;
  })();
  const decimalText = formatPercent(clamped / 100);
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-13961" data-mp="1tiqbtkuccvov">
      {label && <div className="mb-5 text-center" data-id="element-13962" data-mp="278ts7v6jhv6e">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-blue-200 shadow-sm" data-id="element-13963" data-mp="22bbsmveewsb2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-13964" data-mp="2h8uffs7j4vfv" />
            <span className="text-sm font-bold text-blue-900" data-id="element-13965" data-mp="2c9ra06nn0n7d">{label}</span>
          </div>
        </div>}

      <div className="space-y-4" data-id="element-13966" data-mp="mu9x3m6olbzn">
        <SingleBar percentage={clamped} color="bg-blue-500" barLabel={percentageLabel} showValue={showValue} data-id="element-13967" data-mp="mvcodv7x1gg3" />
        

        {hasCompare && <SingleBar percentage={clamp(comparePercentage!, 0, 100)} color="bg-amber-500" barLabel={compareLabel} showValue={showValue} delay={0.2} data-id="element-13968" data-mp="hxtbozqv9bq9" />}

        {/* Educational explanation */}
        <div className="bg-white/80 rounded-xl p-4 border border-blue-100 shadow-sm" data-id="element-13969" data-mp="2yjzoh0xrssb9">
          <div className="flex items-start gap-3" data-id="element-13970" data-mp="2jsh995cqj1sy">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-13971" data-mp="2ork642ika1pu">
              <span className="text-indigo-600 text-sm" data-id="element-13972" data-mp="2as2n63ry5ioe">📊</span>
            </div>
            <div data-id="element-13973" data-mp="2etnu78hsv2qo">
              <h4 className="text-sm font-bold text-indigo-900 mb-1" data-id="element-13974" data-mp="2eu5zeumfwraq">
                Wat betekent dit?
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13975" data-mp="20vpjqe6lnqgg">
                <strong data-id="element-13976" data-mp="25sg0l3q8aq2x">{formatPercent(clamped)}%</strong> betekent{' '}
                <strong data-id="element-13977" data-mp="3v85xe01q66vc">{formatPercent(clamped)}</strong> van de{' '}
                <strong data-id="element-13978" data-mp="3v8yvkcmmbo1v">100</strong>.
                {fractionText && <span data-id="element-13979" data-mp="424krn24357l">
                    {' '}
                    Als breuk is dat <strong data-id="element-13980" data-mp="2kpeyi9hmxi6">{fractionText}</strong>.
                  </span>}
                <span data-id="element-13981" data-mp="2m0jwleo6bav">
                  {' '}
                  Als kommagetal is dat <strong data-id="element-13982" data-mp="3tpz11yud2rmi">{decimalText}</strong>.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}